import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { removeFromCart, updateQuantity } from '../store/cartSlice';
import { addToWishlist } from '../store/wishlistSlice';
import { FiTrash2, FiHeart, FiShoppingBag, FiTag, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './Cart.css';

const COUPONS = [
  { code: 'FIRST20', discount: 20, type: 'percent', desc: '20% off on first order' },
  { code: 'SAVE200', discount: 200, type: 'flat', desc: 'Flat ₹200 off on orders above ₹1999' },
  { code: 'FREEDEL', discount: 0, type: 'delivery', desc: 'Free delivery on this order' },
];

export default function Cart() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const items = useSelector(s => s.cart.items);
  const [coupon, setCoupon] = React.useState('');
  const [appliedCoupon, setAppliedCoupon] = React.useState(null);
  const [couponError, setCouponError] = React.useState('');

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const originalTotal = items.reduce((sum, i) => sum + (i.originalPrice || i.price) * i.quantity, 0);
  const totalDiscount = originalTotal - subtotal;
  const deliveryCharge = subtotal >= 499 ? 0 : 49;
  const couponDiscount = appliedCoupon
    ? appliedCoupon.type === 'percent' ? Math.round(subtotal * appliedCoupon.discount / 100)
      : appliedCoupon.type === 'flat' ? appliedCoupon.discount
      : 0
    : 0;
  const deliveryDiscount = appliedCoupon?.type === 'delivery' ? deliveryCharge : 0;
  const total = subtotal + (deliveryCharge - deliveryDiscount) - couponDiscount;

  const handleRemove = (item) => {
    dispatch(removeFromCart(item.cartId));
    toast.info('Item removed from bag');
  };

  const handleMoveToWishlist = (item) => {
    dispatch(addToWishlist(item));
    dispatch(removeFromCart(item.cartId));
    toast.success('Moved to wishlist!');
  };

  const handleApplyCoupon = () => {
    const found = COUPONS.find(c => c.code === coupon.toUpperCase());
    if (found) {
      if (found.type === 'flat' && subtotal < 1999) {
        setCouponError('Minimum order amount ₹1999 required');
        return;
      }
      setAppliedCoupon(found);
      setCouponError('');
      toast.success(`Coupon "${found.code}" applied!`);
    } else {
      setCouponError('Invalid coupon code');
    }
  };

  if (items.length === 0) return (
    <div className="cart-empty-page">
      <div className="cart-empty">
        <FiShoppingBag className="cart-empty-icon" />
        <h2>Your bag is empty!</h2>
        <p>Add items to it now.</p>
        <Link to="/products" className="btn btn-primary btn-lg">Shop Now</Link>
      </div>
    </div>
  );

  return (
    <div className="cart-page">
      <div className="cart-layout">
        {/* Items */}
        <div className="cart-items-section">
          <div className="cart-header">
            <h2>My Bag <span>({items.length} items)</span></h2>
          </div>

          {/* Delivery Address */}
          <div className="cart-delivery-bar">
            <span>📍 Deliver to: <strong>Select Address</strong></span>
            <button onClick={() => navigate('/profile?tab=address')} className="change-btn">Change <FiChevronRight /></button>
          </div>

          {/* Free delivery progress */}
          {subtotal < 499 && (
            <div className="free-delivery-bar">
              <p>Add items worth <strong>₹{499 - subtotal}</strong> more for <span>FREE delivery</span></p>
              <div className="progress-bar"><div className="progress-fill" style={{ width: `${(subtotal / 499) * 100}%` }} /></div>
            </div>
          )}

          {items.map(item => (
            <div key={item.cartId} className="cart-item">
              <div className="cart-item-image" onClick={() => navigate(`/product/${item.id}`)}>
                <span className="cart-item-emoji">{item.imageEmoji || '👗'}</span>
                {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
              </div>
              <div className="cart-item-info">
                <div className="cart-item-top">
                  <div>
                    <p className="cart-item-brand">{item.brand}</p>
                    <p className="cart-item-name">{item.name}</p>
                    <div className="cart-item-meta">
                      <span>Size: <strong>{item.selectedSize}</strong></span>
                      {item.selectedColor && <span>Color: <strong>{item.selectedColor}</strong></span>}
                    </div>
                  </div>
                  <div className="cart-item-price-col">
                    <span className="cart-item-price">₹{(item.price * item.quantity).toLocaleString()}</span>
                    {item.originalPrice && (
                      <span className="cart-item-original">₹{(item.originalPrice * item.quantity).toLocaleString()}</span>
                    )}
                    {item.originalPrice && item.originalPrice > item.price && (
                      <span className="cart-item-savings">You save ₹{((item.originalPrice - item.price) * item.quantity).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <div className="cart-item-actions">
                  <div className="qty-control">
                    <button onClick={() => item.quantity > 1 ? dispatch(updateQuantity({ cartId: item.cartId, quantity: item.quantity - 1 })) : handleRemove(item)}>−</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => dispatch(updateQuantity({ cartId: item.cartId, quantity: item.quantity + 1 }))}>+</button>
                  </div>
                  <button className="cart-action-btn" onClick={() => handleMoveToWishlist(item)}>
                    <FiHeart /> Move to Wishlist
                  </button>
                  <button className="cart-action-btn remove" onClick={() => handleRemove(item)}>
                    <FiTrash2 /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Coupon Section */}
          <div className="coupon-section">
            <div className="coupon-header"><FiTag /> <h3>Apply Coupon</h3></div>
            <div className="coupon-input-row">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={coupon}
                onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponError(''); }}
              />
              <button onClick={handleApplyCoupon} disabled={!coupon}>APPLY</button>
            </div>
            {couponError && <p className="coupon-error">{couponError}</p>}
            {appliedCoupon && (
              <div className="coupon-applied">
                ✅ <strong>{appliedCoupon.code}</strong> applied — {appliedCoupon.desc}
                <button onClick={() => { setAppliedCoupon(null); setCoupon(''); }}>Remove</button>
              </div>
            )}
            <div className="coupon-suggestions">
              <p className="coupon-suggest-title">Available Coupons</p>
              {COUPONS.map(c => (
                <div key={c.code} className="coupon-chip" onClick={() => setCoupon(c.code)}>
                  <span className="coupon-code">{c.code}</span>
                  <span className="coupon-desc">{c.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Price Summary */}
        <div className="cart-summary">
          <div className="summary-card">
            <h3>PRICE DETAILS ({items.length} Items)</h3>
            <div className="summary-divider" />
            <div className="summary-rows">
              <div className="summary-row">
                <span>Total MRP</span>
                <span>₹{originalTotal.toLocaleString()}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="summary-row discount">
                  <span>Discount on MRP</span>
                  <span>−₹{totalDiscount.toLocaleString()}</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="summary-row discount">
                  <span>Coupon Discount</span>
                  <span>−₹{couponDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Delivery Charge</span>
                {deliveryCharge === 0 || deliveryDiscount > 0 ? (
                  <span className="free-delivery">FREE</span>
                ) : (
                  <span>₹{deliveryCharge}</span>
                )}
              </div>
            </div>
            <div className="summary-divider" />
            <div className="summary-row total">
              <span>Total Amount</span>
              <span>₹{total.toLocaleString()}</span>
            </div>
            {(totalDiscount + couponDiscount + deliveryDiscount) > 0 && (
              <p className="savings-banner">
                🎉 You are saving ₹{(totalDiscount + couponDiscount + deliveryDiscount).toLocaleString()} on this order!
              </p>
            )}
            <button className="place-order-btn" onClick={() => navigate('/checkout')}>
              PLACE ORDER
            </button>
            <div className="secure-payment">
              <span>🔒</span> Safe and Secure Payments. 100% Authentic Products.
            </div>
          </div>

          {/* Offers */}
          <div className="offers-card">
            <h4>Available Offers</h4>
            <ul>
              <li>💳 10% instant discount with HDFC Bank Credit Card</li>
              <li>🏦 No Cost EMI on orders above ₹3000</li>
              <li>🎁 Extra 5% off with HUGE Insider</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
